import 'react-block-ui/style.css';

import * as React from 'react';
import * as ReactDOM from 'react-dom';

import { DefaultButton, Dialog, DialogFooter, DialogType, Dropdown, IDropdownOption, PrimaryButton, TextField, MessageBar, MessageBarType } from '@fluentui/react';
import { Payer, Visit } from '../../../redux/visits/types';

import BlockUi from 'react-block-ui';
import { HealAPI } from '../../../healApi';
import { StatusOptions } from '../../../types';
import { ToastParams } from '../../../redux/message/types';

export interface AddInsuranceDialogProps {
  visit: Visit;
  payers: Payer[];
  onClosed?: (success: boolean) => void;
  actions: {
    toast: (params: ToastParams) => void;
  };
}

export interface AddInsuranceDialogState {
  date?: Date;
  error?: string;
  isSaving: boolean;
  payerId: string;
  memberId: string;
  groupId: string;
}

export class AddInsuranceDialog {
  public static show(options: AddInsuranceDialogProps) {
    const dialogNode = document.createElement('div');
    const close = (): void => {
      ReactDOM.unmountComponentAtNode(dialogNode);
      dialogNode.remove();
    };
    ReactDOM.render(<AddInsuranceDialogInternal {...options} close={close} />, dialogNode);
  }
}

class AddInsuranceDialogInternal extends React.Component<AddInsuranceDialogProps & { close: () => void }, AddInsuranceDialogState> {
  constructor(props: any) {
    super(props);

    this.state = {
      isSaving: false,
      payerId: props.visit.insurancePayerId || '',
      memberId: props.visit.insuranceMemberId || '',
      groupId: props.visit.insuranceGroupId || '',
    };
  }

  public render(): JSX.Element {
    return (
      <Dialog
        hidden={false}
        dialogContentProps={{
          type: DialogType.largeHeader,
          title: (!this.props.visit.insuranceName ? 'Add' : 'Update') + ' Insurance',
        }}
        onDismiss={() => this.close()}
        modalProps={{
          isBlocking: true,
        }}
      >
        <BlockUi tag="div" blocking={this.state.isSaving}>
          {this.state.error && (
            <MessageBar messageBarType={MessageBarType.error} styles={{ root: { marginBottom: 15 } }}>
              {this.state.error}
            </MessageBar>
          )}
          <Dropdown
            label="Insurance company"
            defaultSelectedKey={this.state.payerId}
            options={this.payersForDropdown(this.props.payers)}
            onChange={(ev, o) => o && this.setState({ payerId: o.key as string, memberId: '', groupId: '' })}
          />
          <TextField label="Member id" value={this.state.memberId} disabled={!this.state.payerId} onChange={(ev, v) => this.setState({ memberId: v || '' })} />
          <TextField label="Group id" value={this.state.groupId} disabled={!this.state.payerId} onChange={(ev, v) => this.setState({ groupId: v || '' })} />
          <DialogFooter>
            <PrimaryButton onClick={this.onAdd} disabled={!this.state.payerId || !this.state.memberId}>
              Ok
            </PrimaryButton>
            <DefaultButton onClick={() => this.close()}>Cancel</DefaultButton>
          </DialogFooter>
        </BlockUi>
      </Dialog>
    );
  }

  private payersForDropdown(payers: Payer[]): IDropdownOption[] {
    return [{ key: '', text: 'No Insurance' }].concat(
      payers.map((payer) => {
        return { key: payer.id, text: payer.longName };
      })
    );
  }

  private close = (success = false) => {
    if (this.props.onClosed) {
      this.props.onClosed(success);
    }
    this.props.close();
  };

  private onAdd = () => {
    this.setState({ isSaving: true });
    HealAPI.updateInsurance(this.props.visit.visitCode, this.state.payerId!, this.state.memberId!, this.state.groupId!, false)
      .then((response) => {
        this.setState({ isSaving: false });
        if (response.ok) {
          if (response.data.status === StatusOptions.OK || !response.data.status) {
            if (response.data.insuranceResponse && response.data.insuranceResponse.status === 'PAYER_UNABLE_TO_RESPOND') {
              this.props.actions.toast({
                type: 'warning',
                title: 'Warning',
                content: response.data.insuranceResponse.message,
              });
            } else {
              this.props.actions.toast({
                type: 'success',
                title: 'Success',
                content: 'Successfully Updated Insurance',
              });
            }
            this.close(true);
          }
        } else {
          this.setState({ error: response.data.description });
        }
      })
      .catch(() => this.setState({ isSaving: false }));
  };
}
